import {inject, Injectable} from '@angular/core';
import {Hanko, register, State} from '@teamhanko/hanko-elements';
import {HANKO_OPTIONS, HANKO_URL} from './inj-token';
import {BehaviorSubject, Observable} from 'rxjs';
import {createMyIdentityFromClaims, createMyIdentityFromUser, CustomClaims, LoginCheckApiResponse, LogoutApiResponse, LogoutExternalClient, MyIdentity} from './model';
import {Preferences} from '@capacitor/preferences';
import {Browser} from '@capacitor/browser';
import {HttpClient, HttpContext} from '@angular/common/http';
import {SOPI_SKIP_HTTP_AUTH_KEY} from './interceptor';
import {Capacitor} from '@capacitor/core';


@Injectable({providedIn: 'root'})
export class HankoService {
    private readonly httpClient = inject(HttpClient);

    private readonly isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject(
        false
    );
    public isLoggedIn$ = this.isLoggedInSubject.asObservable();

    get isLoggedIn(): boolean {
        return this.isLoggedInSubject.value;
    }

    private claims: CustomClaims | null = null;

    private readonly myIdentitySubject: BehaviorSubject<MyIdentity | null> = new BehaviorSubject(null);
    public myIdentity$ = this.myIdentitySubject.asObservable();

    get myIdentity(): MyIdentity | null {
        return this.myIdentitySubject.value;
    }

    private readonly url = inject(HANKO_URL);
    private readonly options = inject(HANKO_OPTIONS);
    private readonly hanko: Hanko;

    constructor() {

        // Prüfen, ob schon ein Login-Flow im Gange ist, und wenn ja, ob man sich im thirdparty State befindet.
        // Wenn ja, und die App wird auf einer Mobile Plattform ausgeführt, wird ein sauberer Start durchgeführt,
        // um mögliche Probleme zu vermeiden.
        const hankoFlowState = State.readFromLocalStorage('hanko-login-flow-state');
        if (hankoFlowState?.flow_name === 'login') {
            if (hankoFlowState.name === 'thirdparty') {
                if (Capacitor.isNativePlatform()) {
                    console.log('App restart and is thirdparty and is mobile -> cleanStart');
                    this.cleanStart();
                }
            }
        }

        this.hanko = new Hanko(this.url, {
            lang: 'de'
        });

        this.hanko.onSessionCreated((d) => {
            console.log('Session created with JWT claims:', d.claims);
            this.claims = d.claims;
            this.isLoggedInSubject.next(true);
            this.myIdentitySubject.next(createMyIdentityFromClaims(d.claims));
            this.getUserData().then(user => {
                if (user) {
                    console.log('User profile:', user.user_id);
                }
            }).catch(error => console.error('Error in onSessionCreated while getting user data:', error));
        });

        this.hanko.onSessionExpired(() => {
            console.log('Session expired');
            this.claims = null;
            this.isLoggedInSubject.next(false);
        });

        this.hanko.onUserLoggedOut(() => {
            console.log('User logged out successfully');
            this.isLoggedInSubject.next(false);
        });

        this.hanko.onUserDeleted(() => {
            console.log('User account deleted');
        });

    }

    public get instance(): Hanko {
        return this.hanko;
    }

    public setLang(lang: string) {
        console.log('Set language to:', lang);
        this.hanko.setLang(lang);
    }

    public async startLogin(cleanStart = false): Promise<void> {
        console.log('startLogin: clean', cleanStart);

        const hankoFlowState = State.readFromLocalStorage('hanko-login-flow-state');

        if (hankoFlowState?.flow_name === 'login') {
            if (hankoFlowState.name === 'thirdparty') {
                try {
                    const url = globalThis.location.href;
                    const searchParams = new URL(url).searchParams;
                    const hankoToken = searchParams.get('hanko_token');
                    if (!hankoToken) {
                        console.log('cleanStart true because no hanko_token in url');
                        cleanStart = true;
                    }
                } catch (error) {
                    console.warn('Could not parse URL for hanko_token check:', error);
                    // Bei Fehler beim URL-Parsing sicherheitshalber cleanStart durchführen
                    console.log('cleanStart true because URL parsing failed');
                    cleanStart = true;
                }
            }
        }

        if (cleanStart) {
            await this.cleanStart();
        }

        const st = await this.hanko.createState('login', {
            cacheKey: 'hanko-login-flow-state',
            loadFromCache: true,
            excludeAutoSteps: ['thirdparty'],
        });

        console.log('Login Flow Startet:', st.flowName);
        st.saveToLocalStorage();
    }

    register() {
        register(this.url).catch((error) => {
            console.error(error);
        });
    }

    public getAccessToken() {
        return this.hanko.getSessionToken();
    }

    public async cleanStart() {
        console.log('clear states');
        localStorage.removeItem('hanko-login-flow-state');
        localStorage.removeItem('hanko-auth-flow-state');
        localStorage.removeItem('hanko_session_state');
        await this.deleteCodeVerifier();
    }

    public async logout(externals?: LogoutExternalClient[]) {

        await this.hanko.logout();
        this.hanko.client.sessionTokenStorage.removeSessionToken();

        localStorage.removeItem('hanko-login-flow-state');
        localStorage.removeItem('hanko-auth-flow-state');
        localStorage.removeItem('hanko_session_state');

        if (externals) {

            for (const element of externals) {
                if (element.type === 'microsoft') {

                    const url = new URL(
                        'https://login.microsoftonline.com/common/oauth2/v2.0/logout'
                    );
                    url.searchParams.append('client_id', element.client_id);

                    // if (mail != null) {
                    //   url.searchParams.append('logout_hint', mail);
                    // }
                    // if (msid != null) {
                    //   url.searchParams.append('logout_hint', msid);
                    // }

                    await Browser.open({url: url.toString()});
                }
            }
        }

        console.log('User logged out');
    }

    public async getUserData() {
        try {
            // getCurrentUser() ist in der SDK 3.0.0+ verfügbar
            const user = await this.hanko.getCurrentUser();

            if (user) {
                console.log('User profile:', user.user_id, user.emails);
                this.myIdentitySubject.next(createMyIdentityFromUser(user));
            }

            return user || null;
        } catch (error) {
            // Fehler stumm ignorieren - die Authentifizierung funktioniert trotzdem über Session Claims
            // Der User-Endpunkt kann manchmal nicht verfügbar sein, aber das ist nicht kritisch
            return null;
        }
    }

    public async validateSession() {
        const session = await this.hanko.validateSession();
        this.claims = session.claims as CustomClaims;

        this.isLoggedInSubject.next(session.is_valid);
        if (session.is_valid) {
            this.isLoggedInSubject.next(true);
            this.myIdentitySubject.next(createMyIdentityFromClaims(session.claims));
        } else {
            this.isLoggedInSubject.next(false);
            this.myIdentitySubject.next(null);
        }

        console.log('Session valid:', session.is_valid, 'Claims:', session.claims);
        return session;
    }

    public getClaims() {
        return this.claims;
    }

    public async checkIsLoggedIn() {
        const session = await this.validateSession();
        return session.is_valid;
    }

    public async createCodeVerifier(): Promise<string> {
        // https://pilcrow.vercel.app/blog/random-values-typescript
        const bytes = new Uint8Array(20);
        crypto.getRandomValues(bytes);
        const id = btoa(String.fromCodePoint(...bytes)); // consider using base64url too


        await Preferences.set({
            key: 'hanko_code_verifier',
            value: id,
        });
        return id;
    }

    public saveLoginIdentifier(value: string) {
        return Preferences.set({
            key: 'hanko_login_identifier',
            value,
        });
    }

    public clearLoginIdentifier() {
        return Preferences.remove({
            key: 'hanko_login_identifier'
        });
    }

    async getLoginIdentifier() {
        const value = await Preferences.get({
            key: 'hanko_login_identifier',
        });

        if (value?.value != null) {
            return value.value;
        }
        return null;
    }

    public async getCodeVerifier() {
        const value = await Preferences.get({
            key: 'hanko_code_verifier',
        });

        if (value?.value == null) {
            throw new Error('no code verifier found');
        }
        return value.value;
    }

    public deleteCodeVerifier() {

        return Preferences.remove({
            key: 'hanko_code_verifier',
        });
    }


    public apiLoginCheck(mail: string | null): Observable<LoginCheckApiResponse> {

        return this.httpClient.get<LoginCheckApiResponse>(`${this.options.apiUrl}/${this.options.loginCheckApiUrl}?email=${mail}`, {
            context: new HttpContext().set(SOPI_SKIP_HTTP_AUTH_KEY, true) // skip interceptor, falls noch ein alter token da ist
        });

        // if (mail != null) {
        //   if (mail.endsWith('@softwarepioniere.de')) {
        //     return of({
        //       use_entra_id: true,
        //       entra_id_domain: 'softwarepioniere.de',
        //     });
        //   }
        //   if (mail.endsWith('@sopidemo.de')) {
        //     return of({
        //       use_entra_id: true,
        //       entra_id_domain: 'sopidemo.de',
        //     });
        //   }
        //   if (mail.endsWith('@orgarena.de')) {
        //     return of({
        //       use_entra_id: true,
        //       entra_id_domain: 'orgarena.de',
        //     });
        //   }
        // }
        // return of({
        //   use_entra_id: false,
        // });
    }

    public apiLogout(): Observable<LogoutApiResponse> {
        return this.httpClient.post<LogoutApiResponse>(`${this.options.apiUrl}/${this.options.logoutApiUrl}`, {});
    }


}
